/* ==========================================================================
   Volumetric Efficiency Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-vec]');
  if (!root) return;

  /* A four stroke engine completes one intake stroke every two revolutions,
     so it ingests half its displacement per revolution. The 3,456 constant
     folds in that factor of two and the conversion from cubic inches per
     minute to cubic feet per minute. */
  var CONSTANT = 3456;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.cid) || input.cid <= 0) {
      return { ok: false, errors: ['Enter engine displacement in cubic inches.'] };
    }
    if (!isFinite(input.rpm) || input.rpm <= 0) {
      return { ok: false, errors: ['Enter engine speed in rpm.'] };
    }

    var figures = [], rows = [], status, warnings = [];
    var solving = isFinite(input.knownCfm) && input.knownCfm > 0;

    if (solving) {
      var solvedVe = (input.knownCfm * CONSTANT) / (input.cid * input.rpm) * 100;
      figures.push({ label: 'Volumetric efficiency', value: num(solvedVe, 1) + '%',
        sub: 'From ' + comma(Math.round(input.knownCfm)) + ' CFM at ' + comma(input.rpm) + ' rpm.' });
      figures.push({ label: 'Airflow', value: comma(Math.round(input.knownCfm)) + ' CFM',
        sub: 'As entered.' });

      if (solvedVe > 100) {
        status = { key: 'good', label: num(solvedVe, 1) + '% VE',
                   note: 'Over 100%. Either the engine is boosted or the intake is genuinely well tuned at this rpm.' };
      } else if (solvedVe >= 90) {
        status = { key: 'good', label: num(solvedVe, 1) + '% VE',
                   note: 'Strong for a naturally aspirated engine. Good heads and induction.' };
      } else if (solvedVe >= 75) {
        status = { key: 'monitor', label: num(solvedVe, 1) + '% VE',
                   note: 'Typical of a stock or lightly modified engine.' };
      } else {
        status = { key: 'soon', label: num(solvedVe, 1) + '% VE',
                   note: 'Low. Either the engine is well off its torque peak, or something is restricting it.' };
      }
      rows.push({ head: 'Solved' });
      rows.push({ label: 'Measured airflow', value: comma(Math.round(input.knownCfm)) + ' CFM' });
      rows.push({ label: 'Volumetric efficiency', value: num(solvedVe, 1) + '%' });
    } else {
      var ve = isFinite(input.ve) && input.ve > 0 ? input.ve : 85;
      var cfm = (input.cid * input.rpm * (ve / 100)) / CONSTANT;
      figures.push({ label: 'Airflow required', value: comma(Math.round(cfm)) + ' CFM',
        sub: 'At ' + comma(input.rpm) + ' rpm and ' + num(ve, 0) + '% volumetric efficiency.' });
      figures.push({ label: 'At 100% VE', value: comma(Math.round((input.cid * input.rpm) / CONSTANT)) + ' CFM',
        sub: 'The theoretical maximum for this displacement and rpm.' });

      status = { key: 'good', label: comma(Math.round(cfm)) + ' CFM',
                 note: num(ve, 0) + '% VE on ' + comma(Math.round(input.cid)) + ' cubic inches at ' + comma(input.rpm) + ' rpm.' };

      rows.push({ head: 'Airflow' });
      rows.push({ label: 'At ' + num(ve, 0) + '% VE', value: comma(Math.round(cfm)) + ' CFM' });
      rows.push({ label: 'At 100% VE', value: comma(Math.round((input.cid * input.rpm) / CONSTANT)) + ' CFM' });
      rows.push({ head: 'Across the range' });
      [3000, 4000, 5000, 6000, 7000].forEach(function (r) {
        rows.push({ label: comma(r) + ' rpm', value: comma(Math.round((input.cid * r * (ve / 100)) / CONSTANT)) + ' CFM' });
      });
    }

    rows.push({ head: 'Reference' });
    rows.push({ label: 'Stock engine', value: '75 to 85% VE' });
    rows.push({ label: 'Built naturally aspirated', value: '90 to 105% VE' });
    rows.push({ label: 'Boosted', value: 'well over 100%' });
    rows.push({ label: 'Displacement', value: comma(Math.round(input.cid)) + ' ci  (' + num(input.cid / 61.0237, 1) + ' L)' });

    if (!solving && input.ve > 110) {
      warnings.push('Volumetric efficiency above 110% naturally aspirated is not realistic outside a narrow rpm band on a purpose built engine. Check whether you meant to model a boosted setup.');
    }

    return { ok: true, errors: [], warnings: warnings, status: status, figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "cid",
          "key": "c",
          "type": "number"
      },
      {
          "name": "rpm",
          "key": "r",
          "type": "number"
      },
      {
          "name": "ve",
          "key": "v",
          "type": "number"
      },
      {
          "name": "knownCfm",
          "key": "k",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-vec__figure">'
        + '<div class="pc-vec__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-vec__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-vec__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-vec__row pc-vec__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-vec__row">'
        + '<span class="pc-vec__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-vec__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_VEC = { compute: compute };
})();
